import React from 'react';

import noop from 'lodash/noop';

import {TComponentRef, formField, newFormField} from '../FormField';
import {HiddenInput} from '../primitives';
import * as S from './style';

type TValue = any;
type THandler = (value: TValue) => void;
export type TProps = TComponentRef &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value'> & {
    value?: TValue;
    onChange?: THandler;
    onFocus?: THandler;
    onBlur?: THandler;
    labelComponent?: TAnyComponent;
    isInvalid?: boolean;
    inputValue?: TValue;
    selector?: string;
  };

const Radio = React.memo<TProps>(
  ({
    name,
    children,
    className,
    id: propId,
    value,
    isInvalid,
    onChange = noop,
    onFocus = noop,
    onBlur = noop,
    checked,
    disabled,
    readOnly,
    labelComponent: Label = S.Label,
  }) => {
    const stringValue = JSON.stringify(value);
    const id = `${propId || name}/${stringValue}`;

    return (
      <S.Radio htmlFor={id} className={className} disabled={disabled} readOnly={readOnly}>
        <HiddenInput
          id={id}
          type="radio"
          name={name}
          value={stringValue}
          // onClick потому что событие onChange срабатывает не всегда
          onClick={disabled || readOnly ? undefined : () => onChange(value)}
          onFocus={disabled ? undefined : onFocus}
          onBlur={disabled ? undefined : onBlur}
          disabled={disabled}
          readOnly={readOnly}
        />
        <S.ControlWrapper>
          <S.Control
            checked={checked}
            disabled={disabled}
            isInvalid={isInvalid}
            readOnly={readOnly}
          />
        </S.ControlWrapper>
        <Label>{children}</Label>
      </S.Radio>
    );
  },
);

export const NewRadio = React.memo<TProps>(
  ({
    name,
    children,
    className,
    id: propId,
    value,
    isInvalid,
    onChange = noop,
    onFocus = noop,
    onBlur = noop,
    disabled,
    readOnly,
    labelComponent: Label = S.Label,
    checked,
    innerRef,
    inputValue,
    selector,
  }) => {
    const stringValue = JSON.stringify(inputValue);
    const id = `${propId || name}/${inputValue}`;

    checked = checked ?? inputValue === value;

    return (
      <S.Radio
        data-selector={selector}
        htmlFor={id}
        className={className}
        disabled={disabled}
        readOnly={readOnly}
      >
        <HiddenInput
          key={`${id}-${checked}`}
          id={id}
          type="radio"
          name={name}
          ref={innerRef}
          value={stringValue}
          // onClick потому что событие onChange срабатывает не всегда
          onClick={disabled || readOnly ? undefined : () => onChange(inputValue)}
          onFocus={disabled ? undefined : onFocus}
          onBlur={disabled ? undefined : onBlur}
          disabled={disabled}
          readOnly={readOnly}
          checked={checked}
        />
        <S.ControlWrapper>
          <S.Control
            checked={checked}
            disabled={disabled}
            isInvalid={isInvalid}
            readOnly={readOnly}
          />
        </S.ControlWrapper>
        {children && <Label>{children}</Label>}
      </S.Radio>
    );
  },
);

export default Radio;

export const RadioField = formField<TProps, TValue>(Radio, {
  showError: false,
});

export const NewRadioField = newFormField<TProps>(NewRadio, {
  showError: false,
});
